/**
 * Textos de la identidad frente a OFAC en el dibujo del Registro Público de Comercio: por qué una persona o empresa del
 * registro se toma como la que está en la lista, qué la contradice y qué queda a revisión.
 *
 * 2026-10-02 (nombres casi iguales, ronda de revisión): las frases que dicen «nombre … igual» sólo valen si el nombre
 * es exacto (`nombre_exacto`). Si el nombre es casi igual, parcial o fonético, la frase dice eso y agrega el dato que
 * coincide. Las cuatro señales nuevas usan los textos que aprobó Bernardo el 2026-10-02 (bitácora
 * docs/sessions/2026-10-02-identidad-casi-iguales.md), en minúscula inicial y sin punto porque van dentro de una frase.
 *
 * Etapa 4, ajustes 4d (2026-10-05): se movió aquí desde app/nexo-frontend/src/ui/senales-siger.ts (que ahora sólo reexporta) para que
 * el dibujo de la red de Argus Team use los mismos textos. Puro: sin React, sin DOM y sin llamadas a la red.
 */

/** Señal → etiqueta corta del chip (en orden de fuerza) y explicación larga. */
export const BASE: [string, string, string][] = [
  ["curp_igual", "CURP", "la CURP impresa en el acta es igual a la de OFAC"],
  ["rfc_igual", "RFC", "el RFC completo (con homoclave) es igual al de OFAC"],
  ["folio_mercantil_igual", "FOLIO", "OFAC cita el mismo folio en el Registro Público de Comercio y la razón social coincide"],
  ["folio_mercantil_digito_extra", "FOLIO", "el folio en el Registro Público de Comercio que cita OFAC (con un dígito de más) y la razón social coincide"],
  ["cud_igual", "CLAVE ÚNICA DEL DOCUMENTO", "la clave única del documento del Registro Público de Comercio coincide"],
  ["fecha_nacimiento_impresa_igual", "NOMBRE + F. NAC.", "nombre completo y fecha de nacimiento impresa iguales a OFAC"],
  // la persona que OFAC nombra (textos aprobados por Bernardo el 2026-10-16): sólo de empresas; confirma con la denominación exacta
  ["persona_nombrada", "PERSONA QUE OFAC NOMBRA", "una persona socia o administradora en sus actas a la que OFAC relaciona con la empresa"],
  ["rfc_raiz_igual", "RFC S/HOMOCLAVE", "la raíz del RFC (iniciales y fecha) coincide; sin homoclave no confirma"],
  ["fecha_nacimiento_derivada_igual", "NOMBRE + F. NAC. (RFC)", "la fecha que codifica el RFC/CURP coincide con OFAC"],
  ["rfc_errata_fecha_igual", "F. NAC. (RFC CON ERRATA)", "el RFC del acta está mal escrito, pero sin el dígito de más da la fecha de nacimiento de la lista de OFAC"],
  ["fecha_constitucion_igual_rfc_ofac", "NOMBRE + F. CONSTITUCIÓN", "la fecha de constitución que indica el RFC que publica OFAC"],
  ["fecha_establecimiento_ofac_igual", "NOMBRE + F. ESTABLECIMIENTO", "la fecha de establecimiento que registra OFAC"],
  ["relacion_ofac_con_ancla", "RELACIÓN OFAC", "una relación que OFAC registra con otro sancionado ya identificado en el registro"],
  ["empresa_en_c/o_de_ofac", "SANCIONADO EN SU DOMICILIO", "OFAC ubica a un sancionado en el domicilio de esta empresa"],
  ["sociedad_c/o_en_ofac", "SOCIEDAD CON SANCIONADO EN SU DOMICILIO", "participa en una sociedad en cuyo domicilio OFAC ubica a un sancionado"],
  ["sociedad_ligada_en_ofac", "SOCIEDAD RELACIONADA", "participa en una sociedad que OFAC relaciona con esta entidad"],
  ["otro_sancionado_en_la_sociedad", "OTRO SANCIONADO EN LA SOCIEDAD", "en la misma sociedad está otra persona de la lista de OFAC, relacionada con ésta o sancionada el mismo día"],
  // reglas pendientes de r13 (textos aprobados por Bernardo el 2026-10-16)
  ["mismo_comunicado_del_tesoro", "MISMO COMUNICADO DEL TESORO", "el Tesoro la nombra en el mismo comunicado que a la sociedad o a otra persona de la sociedad"],
  ["sociedad_sancionada_el_mismo_dia", "SOCIEDAD SANCIONADA EL MISMO DÍA", "la sociedad en la que participa está en la lista de OFAC y fue sancionada el mismo día que esta persona"],
  ["grupo_sancionado_en_la_sociedad", "GRUPO SANCIONADO EN LA SOCIEDAD", "en la misma sociedad hay otras personas con el nombre de la lista de OFAC, relacionadas con ésta o sancionadas el mismo día"],
  ["buscada_como_entidad_ofac", "BUSCADA POR OFAC", "el acta se descargó al buscar a la entidad sancionada"],
  ["lugar_nacimiento_igual", "NOMBRE + LUGAR", "nombre y lugar de nacimiento concordantes"],
  ["domicilio_ofac_igual", "DOMICILIO", "el domicilio del acta es una dirección que da la lista de OFAC"],
  ["anio_nacimiento_igual", "NOMBRE + AÑO", "nombre y año de nacimiento concordantes"],
  ["nombre_exacto", "NOMBRE", "solo el nombre completo coincide"],
];

/**
 * Nombre que no es exacto, del más cercano al más lejano. Textos y etiquetas cortas aprobados por Bernardo el 2026-10-02
 * (todo dice «la lista de OFAC»).
 */
export const NOMBRE_PARCIAL: [string, string, string][] = [
  ["nombre_casi_igual", "NOMBRE CASI IGUAL", "el nombre es casi igual al de la lista de OFAC: cambia una letra"],
  ["denominacion_sin_articulo", "DENOMINACIÓN SIN ARTÍCULO", "la denominación es la misma que la de la lista de OFAC salvo un artículo («El», «La»)"],
  ["denominacion_casi_igual", "DENOMINACIÓN CASI IGUAL", "la denominación cambia una palabra respecto a la de la lista de OFAC"],
  ["nombre_registro_contenido", "NOMBRE INCOMPLETO", "el acta no trae una de las palabras del nombre de la lista de OFAC"],
  ["nombre_ofac_contenido", "NOMBRE PARCIAL", "el nombre de OFAC está contenido en el del Registro Público de Comercio"],
  ["nombre_variante_fonetica", "NOMBRE CON OTRA GRAFÍA", "una variante fonética del nombre"],
  ["alias_debil", "ALIAS DÉBIL", "coincide con un alias que OFAC marca como débil"],
];

/** Frases de BASE que afirman que el nombre coincide → el dato solo, para cuando el nombre no es exacto. */
const DATO_SIN_NOMBRE: Record<string, [string, string]> = {
  // textos de un solo dato aprobados por Bernardo el 2026-10-02 (decisión c, sin la sigla CUD)
  folio_mercantil_igual: ["FOLIO", "el folio en el Registro Público de Comercio coincide"],
  folio_mercantil_digito_extra: ["FOLIO", "el folio en el Registro Público de Comercio coincide con un dígito de más"],
  cud_igual: ["CLAVE ÚNICA DEL DOCUMENTO", "la clave única del documento del Registro Público de Comercio coincide"],
  fecha_nacimiento_impresa_igual: ["F. NAC.", "la fecha de nacimiento escrita en el acta coincide"],
  fecha_nacimiento_derivada_igual: ["F. NAC. (RFC)", "la fecha que codifica el RFC/CURP coincide con OFAC"],
  fecha_constitucion_igual_rfc_ofac: ["F. CONSTITUCIÓN", "la fecha de constitución que indica el RFC que publica OFAC"],
  fecha_establecimiento_ofac_igual: ["F. ESTABLECIMIENTO", "la fecha de establecimiento que registra OFAC"],
  lugar_nacimiento_igual: ["LUGAR", "el lugar de nacimiento concuerda"],
  anio_nacimiento_igual: ["AÑO", "el año de nacimiento concuerda"],
};

export const CONTRA_ES: Record<string, string> = {
  curp_distinto: "CURP distinta", rfc_distinto: "RFC distinto", rfc_raiz_distinta: "raíz de RFC distinta",
  fecha_nacimiento_distinta: "fecha de nacimiento distinta", sexo_distinto: "sexo distinto",
  rfc_ofac_anterior_a_la_constitucion_siger: "el RFC de OFAC es anterior a la constitución (nombre reutilizado)",
  rfc_ofac_posterior_a_la_sociedad_siger: "la sociedad ya actuaba en el Registro Público de Comercio antes de la fecha del RFC de OFAC",
  // corrida r13 (textos aprobados por Bernardo el 2026-10-16)
  curp_ofac_otro_nombre: "la CURP que da la lista de OFAC corresponde a un primer nombre con otra inicial",
  curp_ofac_otro_estado: "la CURP que da la lista de OFAC dice que nació en otro estado",
};

/** Banderas de revisión. Las tres de nombres casi iguales: textos aprobados por Bernardo el 2026-10-02. */
export const REVISION_ES: Record<string, string> = {
  ancla_solo_por_nombre: "anclada solo por la denominación, sin dato independiente",
  rfc_ofac_anterior_sin_acta_constitutiva: "RFC de OFAC anterior a las actas leídas, que no traen el acta constitutiva",
  extractor_descarta: "el lector del acta la descartó: es otra persona con el mismo nombre",
  entidad_ofac_extranjera: "la entidad OFAC no es mexicana",
  fecha_ofac_distinta_de_su_propio_id: "OFAC da una fecha de nacimiento distinta de la de su propio RFC/CURP",
  rfc_errata_un_caracter: "el RFC difiere en un carácter (probable error de escritura)",
  nombre_casi_igual: "el nombre no es idéntico al de la lista de OFAC: confirmar en el acta que es la misma persona",
  denominacion_sin_articulo: "la denominación difiere de la lista de OFAC solo por un artículo: confirmar en el acta que es la misma empresa",
  denominacion_con_errata: "la denominación difiere de la lista de OFAC en una palabra: confirmar en el acta que es la misma empresa",
  cud_sin_razon_social: "la clave única del documento que cita OFAC aparece en el acta, pero la denominación no coincide",
  relacion_ofac_sin_liga_en_actas: "OFAC la relaciona con otro sancionado identificado, pero las actas no los relacionan",
  curp_ofac_no_cuadra: "la CURP que da la lista de OFAC no cuadra con el nombre o el lugar de nacimiento del acta, aunque otros datos sí coinciden",
  menor_de_edad_al_cargo_segun_ofac: "con la fecha de nacimiento de la lista de OFAC tendría menos de 18 años al tomar el cargo",
  // la persona que OFAC nombra (texto aprobado por Bernardo el 2026-10-16)
  entidad_con_otra_empresa_del_registro: "la misma entidad de la lista de OFAC coincide también con otra empresa del registro: confirmar en el acta cuál es",
};

export const NIVEL_ES: Record<string, string> = {
  confirmada: "confirmada", alta: "alta", media: "solo nombre", contradictoria: "contradictoria", baja: "baja",
  descartada: "descartada",
};

/**
 * ¿Se pinta en rojo, como «En la lista de OFAC», la identidad de un nodo del grafo? Etapa 4 (decisión del coordinador, 2026-10-04): sólo
 * con identidad corroborada (nexo.identidad_corroborada, la regla de la ficha). Desde 20261005120000 (ajustes 4d) la base la manda en el
 * grafo (M7 de la revisión del lote 0b) y manda. Sin el campo (una base anterior: Nexo y la base se despliegan por separado), el respaldo
 * de siempre: sólo `confirmada`; una `alta` sin corroborar nunca va en rojo.
 */
export function rojoDeIdentidad(o: { nivel: string; corroborada?: boolean | null }): boolean {
  return typeof o.corroborada === "boolean" ? o.corroborada : o.nivel === "confirmada";
}

/** Niveles que anclan: los únicos que dicen que la persona o empresa del registro es la de la lista de OFAC. */
export const esAncla = (nivel: string): boolean => nivel === "confirmada" || nivel === "alta";

/**
 * Banderas que sólo documentan (consolidate.py, REVISION_DOCUMENTA): no ponen la identidad en duda, pero la incertidumbre
 * residual se dice siempre (decisión de Bernardo, 2026-10-02): van junto a la base de la identidad, no bajo «A revisión».
 */
export const REVISION_DOCUMENTA = new Set(["fecha_ofac_distinta_de_su_propio_id", "rfc_errata_un_caracter"]);

/** Las banderas que ponen en duda la identidad, bajo «A revisión». */
export function textoRevision(r: string[] | undefined): string {
  const duda = (r ?? []).filter((x) => !REVISION_DOCUMENTA.has(x));
  return duda.length > 0 ? ` A revisión: ${duda.map((x) => REVISION_ES[x] ?? x).join("; ")}.` : "";
}

/** Lo que las banderas que documentan agregan a la base de la identidad. */
function notasDocumenta(r: readonly string[]): string {
  const notas = r.filter((x) => REVISION_DOCUMENTA.has(x)).map((x) => REVISION_ES[x] ?? x);
  return notas.length > 0 ? `; ${notas.join("; ")}` : "";
}

/**
 * Etiqueta corta de una señal (chip), o la señal tal cual si no tiene. Con `senales` (todas las de la identidad), una
 * etiqueta que dice «NOMBRE + …» sólo se usa si el nombre es exacto; si no, se dice el dato solo («F. NAC.»).
 */
export function etiquetaSenal(s: string, senales: readonly string[] = []): string {
  const dato = DATO_SIN_NOMBRE[s];
  if (dato && senales.length > 0 && !senales.includes("nombre_exacto")) return dato[0];
  return BASE.find(([k]) => k === s)?.[1] ?? NOMBRE_PARCIAL.find(([k]) => k === s)?.[1] ?? s;
}

/**
 * La base de la identidad, del dato más fuerte al más débil. Si el nombre no es exacto, una frase de BASE que dice
 * «nombre … igual» no se usa: se dice cómo es el nombre (NOMBRE_PARCIAL) y, si lo hay, el dato que sí coincide.
 */
export function baseOfac(senales: string[], revision: readonly string[] = []): { corta: string; larga: string } {
  const b = baseSinNotas(senales);
  return { corta: b.corta, larga: b.larga + notasDocumenta(revision) };
}

function baseSinNotas(senales: string[]): { corta: string; larga: string } {
  const exacto = senales.includes("nombre_exacto");
  const parcial = exacto ? undefined : NOMBRE_PARCIAL.find(([k]) => senales.includes(k));
  for (const [k, corta, larga] of BASE) {
    if (!senales.includes(k)) continue;
    const dato = DATO_SIN_NOMBRE[k];
    if (!dato || exacto) return { corta, larga };
    if (parcial) return { corta: `${parcial[1]} + ${dato[0]}`, larga: `${parcial[2]}; ${dato[1]}` };
    return { corta: dato[0], larga: dato[1] };
  }
  return parcial ? { corta: parcial[1], larga: parcial[2] } : { corta: "NOMBRE", larga: "coincidencia de nombre" };
}

/** Nota de las identidades que no anclan (media, baja, contradictoria, descartada). */
export const NOTA_NO_ANCLA = "no se usa como ancla";

/** Separa las identidades que anclan de las que no, en el orden en que llegan. */
export function gruposAnclas<T extends { nivel: string }>(xs: readonly T[]): { anclas: T[]; otras: T[] } {
  return { anclas: xs.filter((x) => esAncla(x.nivel)), otras: xs.filter((x) => !esAncla(x.nivel)) };
}
